export const showCookiebotIcon = (): void => {
  const cookieBotWidget = document.getElementById('CookiebotWidget');
  if (!cookieBotWidget) {
    return;
  }

  cookieBotWidget.style.visibility = 'visible';
};

export const hideCookiebotIcon = (): void => {
  const cookieBotWidget = document.getElementById('CookiebotWidget');
  if (!cookieBotWidget) {
    return;
  }

  cookieBotWidget.style.visibility = 'hidden';
};

/*
Consent reading for the Cookiebot "statistics" category.

Datadog RUM and Amplitude both fall under "statistics", so neither may load,
initialise or send anything until Cookiebot reports statistics === true.
Cookiebot is a third-party script: the global can be missing, arrive late, or
never arrive at all, so every read here is defensive and defaults to no consent.

`useCookieConsent` wraps this for components; these plain functions exist for
the callers that cannot use a hook — module-scope boot code (index.tsx) and the
Amplitude track wrapper.
*/

export interface CookiebotConsent {
  stamp: string;
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
  method: string;
}

interface CookiebotWindow extends Window {
  Cookiebot?: {
    consent?: CookiebotConsent;
  };
}

type ConsentListener = (consent: CookiebotConsent | null) => void;
type StatisticsConsentListener = (granted: boolean) => void;

/*
Cookiebot dispatches these on `window`. `CookiebotOnConsentReady` is the one
that covers returning visitors, where a stored consent is replayed without the
banner being shown and no accept/decline event ever fires.
*/
const CONSENT_EVENTS = ['CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'];

// Fallback for a Cookiebot script that loads late, or that fired its events
// before we subscribed. Polling stops as soon as the global shows up.
const POLL_INTERVAL_MS = 300;
const POLL_TIMEOUT_MS = 15000;

export const getCookiebotConsent = (): CookiebotConsent | null => {
  try {
    return (window as CookiebotWindow).Cookiebot?.consent ?? null;
  } catch {
    // A blocked or half-initialised Cookiebot can throw on property access.
    return null;
  }
};

export const hasStatisticsConsent = (): boolean => getCookiebotConsent()?.statistics === true;

// Cookiebot mutates its consent object in place, so changes have to be compared
// by value rather than by reference.
const consentKey = (consent: CookiebotConsent | null): string => {
  return consent === null ? '' : JSON.stringify(consent);
};

/*
Subscribes to the full Cookiebot consent object. The listener is called once
synchronously with the current value (`null` while Cookiebot has not loaded),
then again on every change. Returns an unsubscribe function.
*/
export const subscribeToCookiebotConsent = (listener: ConsentListener): (() => void) => {
  let lastKey: string | null = null;
  let pollId: ReturnType<typeof setInterval> | undefined;

  const emitIfChanged = () => {
    const consent = getCookiebotConsent();
    const key = consentKey(consent);
    if (key === lastKey) {
      return;
    }
    lastKey = key;
    listener(consent);
  };

  const stopPolling = () => {
    if (pollId !== undefined) {
      clearInterval(pollId);
      pollId = undefined;
    }
  };

  CONSENT_EVENTS.forEach((eventName) => window.addEventListener(eventName, emitIfChanged));

  const startedAt = Date.now();
  pollId = setInterval(() => {
    emitIfChanged();
    if (getCookiebotConsent() !== null || Date.now() - startedAt >= POLL_TIMEOUT_MS) {
      stopPolling();
    }
  }, POLL_INTERVAL_MS);

  emitIfChanged();

  return () => {
    stopPolling();
    CONSENT_EVENTS.forEach((eventName) => window.removeEventListener(eventName, emitIfChanged));
  };
};

/*
Narrow subscription for the statistics category only. Fires `true` when
statistics consent is granted and `false` when it is absent, denied or revoked.
The first call is synchronous, so a listener must treat `false` as "make sure
nothing is running" rather than "tear down something that was running".
*/
export const onStatisticsConsentChange = (listener: StatisticsConsentListener): (() => void) => {
  let lastGranted: boolean | null = null;

  return subscribeToCookiebotConsent((consent) => {
    const granted = consent?.statistics === true;
    if (granted === lastGranted) {
      return;
    }
    lastGranted = granted;
    listener(granted);
  });
};
